import React, { useMemo, useCallback, useEffect } from 'react'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import { isBedbankItem, isFlightItem } from 'lib/checkout/checkoutUtils'
import { getFlightsTotal } from 'lib/flights/flightUtils'
import useUserCartBedbankHotelItemData from 'hooks/UserCart/useUserCartBedbankHotelItemData'
import { useAppDispatch } from 'hooks/reduxHooks'
import { userCartItemsDelete, userCartItemsSaveForLater } from 'actions/UserCartActions'
import { useUserCartItemState } from 'hooks/UserCart/useUserCartItemState'

interface Props {
  items: Array<App.Checkout.BedbankHotelItem | App.Checkout.FlightItem>
  tileVariant?: 'default' | 'condensed'
  className?: string
  hideTitleAndDates?: boolean
  hideButtons?: boolean
}

function BedbankHotelTile({
  items,
  tileVariant = 'default',
  className,
  hideTitleAndDates = false,
  hideButtons = false,
}: Props) {
  const dispatch = useAppDispatch()

  // Separate flight and hotel items
  const hotelItem = useMemo(() => {
    return items.find(item => isBedbankItem(item))!
  }, [items])
  const flightItems = useMemo(() => {
    return items.filter(item => isFlightItem(item))
  }, [items])

  const data = useUserCartBedbankHotelItemData(hotelItem, false)
  const { deleted: flightDeleted, saveForLater: flightSaveForLater } = useUserCartItemState(flightItems[0].itemId)

  const formattedDateRange = useMemo(() => {
    if (!hotelItem?.checkIn || !hotelItem?.checkOut) return false
    return `${formatDynamicDateRange(hotelItem.checkIn, hotelItem.checkOut)} · ${hotelItem.duration} nights`
  }, [hotelItem?.checkIn, hotelItem?.checkOut, hotelItem?.duration])

  const formattedOccupancy = useMemo(() => {
    if (!hotelItem?.occupancy) return false
    return formatOccupantsShort(hotelItem.occupancy)
  }, [hotelItem?.occupancy])

  const { publicTotal: hotelPublicTotal, memberTotal: hotelMemberTotal } = useMemo(
    () => getUserCartItemDisplayTotals(data.itemTotals),
    [data?.itemTotals]
  )

  // Calculate flight prices
  const flightTotal = useMemo(() => {
    return flightItems.reduce((total, flightItem) => {
      return total + getFlightsTotal(flightItem)
    }, 0)
  }, [flightItems])
  const numberOfRoomsAssociatedWithFlight = flightItems[0]?.bundledItemIds?.length ?? 1
  const flightTotalPerRoom = flightTotal / numberOfRoomsAssociatedWithFlight

  // Calculate bundled prices (hotel + flight)
  const bundledPublicTotal = hotelPublicTotal + flightTotalPerRoom
  const bundledMemberTotal = hotelMemberTotal === 0 ? 0 : hotelMemberTotal + flightTotalPerRoom

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, false, !!data.error)
  const image = data.package?.images[0] || data.offer?.image

  // Check if all items are saved for later
  const areAllItemsSavedForLater = data.isSavedForLater && flightSaveForLater && !flightDeleted

  const isBookable = useMemo(
    () => !flightDeleted && data.isBookable,
    [flightDeleted, data.isBookable]
  )

  // Get all item IDs from the bundle
  const allItemIds = useMemo(() => items.map(item => item.itemId), [items])

  // Auto-save for later if hotel is not bookable
  useEffect(() => {
    // Only save if explicitly enabled AND item is not bookable AND not already saved/deleted
    if (
      !data.isBookable &&
      !data.isSavedForLater &&
      !data.isDeleted &&
      !data.loading &&
      hotelItem
    ) {
      dispatch(userCartItemsSaveForLater(allItemIds))
    }
  }, [data.isBookable, data.isSavedForLater, data.isDeleted, data.loading, hotelItem, dispatch, allItemIds])

  // if flight items are saved for later then save the hotel item as well
  useEffect(() => {
    if (flightSaveForLater && !flightDeleted && !data.isSavedForLater && !data.isDeleted && hotelItem) {
      dispatch(userCartItemsSaveForLater([hotelItem.itemId]))
    }
  }, [flightSaveForLater, flightDeleted, data.isSavedForLater, data.isDeleted, hotelItem, dispatch])

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    dispatch(userCartItemsSaveForLater(allItemIds))
  }, [dispatch, allItemIds])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    dispatch(userCartItemsDelete(allItemIds))
  }, [dispatch, allItemIds])

  if (data.loading || !hotelItem) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (tileVariant === 'condensed') {
    return (
      <CondensedCartReviewTile
        content={<CondensedCartReviewTileContent
          title={hideTitleAndDates ? undefined : data.offer?.property.name || data.offer?.name || ''}
          dates={hideTitleAndDates ? undefined : formattedDateRange}
          packageTitle={data.package?.name}
          occupancy={formattedOccupancy}
        />}
        imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
        banner={<CartReviewBanner variant={bannerTypeVariant} />}
        onDelete={hideButtons ? undefined : handleDelete}
      />
    )
  }

  const actions = hideButtons ? undefined : (
    <CartReviewTileActions
      isBookable={isBookable}
      saveForLater={areAllItemsSavedForLater}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
    />
  )

  return (
    <CartReviewTile
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      content={<CartReviewTileContent
        title={data.offer?.property.name || data.offer?.name || ''}
        dates={formattedDateRange}
        packageTitle={data.package?.name}
        occupancy={formattedOccupancy}
        loyaltyPointsEarningConfiguration={{
          calculationRequests: [],
          calculationType: 'exact',
        }}
      />}
      price={<CartReviewPriceStack
        price={bundledPublicTotal}
        memberPrice={bundledMemberTotal}
        value={data.itemTotals?.value ?? 0}
        luxPlusOfferConfig={data.offer?.luxPlus}
      />}
      banner={<CartReviewBanner variant={bannerTypeVariant} />}
      isBookable={isBookable}
      saveForLater={areAllItemsSavedForLater}
      actions={actions}
      className={className}
      hideAvailabilityDetails={false}
      disableOverlay={false}
    />
  )
}

export default BedbankHotelTile
